<template>
  <div class="agenda-wrapper">
    <!-- Header élégant -->
    <div class="agenda-header">
      <div class="header-content">
        <div>
          <h1 class="display-6 fw-bold text-dark mb-1">
            <i class="fa-solid fa-calendar-days text-primary me-3"></i>
            Mon Agenda
          </h1>
          <p v-if="studentInfo" class="text-muted fs-5 mb-0">
            <strong>{{ studentInfo.prenom }} {{ studentInfo.nom }}</strong>
            <span class="text-primary mx-2">•</span>
            {{ studentInfo.filiere }} – {{ studentInfo.niveau }}
          </p>
        </div>
        <button @click="refreshAgenda" class="btn btn-outline-primary btn-refresh" :disabled="isLoading">
          <i :class="['fa', isLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt']"></i>
          Actualiser
        </button>
      </div>
    </div>

    <!-- Tabs premium -->
    <div class="card border-0 shadow-sm mt-4">
      <div class="card-body p-4">
        <ul class="nav nav-tabs premium-tabs" role="tablist">
          <li class="nav-item">
            <button :class="['nav-link', { active: activeTab === 'upcoming' }]" @click="activeTab = 'upcoming'">
              <i class="fa-solid fa-clock me-2"></i> Prochains cours
              <span v-if="upcomingCourses.length" class="badge bg-primary rounded-pill ms-2">
                {{ upcomingCourses.length }}
              </span>
            </button>
          </li>
          <li class="nav-item">
            <button :class="['nav-link', { active: activeTab === 'today' }]" @click="activeTab = 'today'; loadTodayCourses()">
              <i class="fa-solid fa-calendar-check me-2"></i> Aujourd'hui
              <span v-if="todayCourses.length" class="badge bg-success rounded-pill ms-2">
                {{ todayCourses.length }}
              </span>
            </button>
          </li>
          <li class="nav-item">
            <button :class="['nav-link', { active: activeTab === 'week' }]" @click="activeTab = 'week'">
              <i class="fa-solid fa-table-cells-large me-2"></i> Semaine
            </button>
          </li>
        </ul>

        <div class="tab-content mt-4">
          <!-- === PROCHAINS COURS === -->
          <div v-if="activeTab === 'upcoming'">
            <div class="row g-4">
              <div class="col-lg-8">
                <h4 class="fw-semibold text-dark mb-4">Prochains 7 jours</h4>

                <div v-if="isLoading" class="text-center py-6">
                  <div class="spinner-border text-primary" style="width: 3rem; height: 3rem;"></div>
                  <p class="mt-3 text-muted">Chargement de votre planning...</p>
                </div>

                <div v-else-if="!upcomingCourses.length" class="alert alert-light border text-center py-5">
                  <i class="fa-solid fa-calendar-xmark fa-3x text-muted mb-3"></i>
                  <p class="mb-0 text-muted">Aucun cours prévu pour les 7 prochains jours</p>
                </div>

                <div v-else>
                  <div v-for="cours in upcomingCourses" :key="cours.id" class="course-card-modern mb-4">
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="flex-grow-1">
                        <h5 class="mb-1 fw-bold text-dark">{{ cours.matiere }}</h5>
                        <p class="text-muted mb-2">
                          <i class="fa-solid fa-user-tie me-2"></i>{{ cours.professeur }}
                        </p>
                        <div class="d-flex flex-wrap gap-3 text-sm text-muted">
                          <span><i class="fa-solid fa-calendar me-2"></i>{{ formatDateFull(cours.dateDebut) }}</span>
                          <span><i class="fa-solid fa-clock me-2"></i>{{ cours.heureDebut }} – {{ cours.heureFin }}</span>
                          <span v-if="!cours.estEnLigne"><i class="fa-solid fa-location-dot me-2"></i>{{ cours.salle || 'Salle à confirmer' }}</span>
                        </div>
                      </div>
                      <span :class="['badge fs-6 px-3 py-2', cours.estEnLigne ? 'bg-success' : 'bg-info']">
                        <i :class="['fa-solid me-1', cours.estEnLigne ? 'fa-video' : 'fa-chalkboard']"></i>
                        {{ cours.estEnLigne ? 'En ligne' : 'Présentiel' }}
                      </span>
                    </div>

                    <!-- Bouton visio intelligent -->
                    <div class="mt-4" v-if="cours.estEnLigne">
                      <template v-if="isCourseActive(cours)">
                        <a :href="cours.lienVisio" target="_blank" rel="noopener noreferrer"
                           class="btn btn-success btn-join shadow-sm">
                          <i class="fa-solid fa-video me-2"></i>
                          Rejoindre la visioconférence
                          <i class="fa-solid fa-arrow-up-right-from-square ms-2"></i>
                        </a>
                      </template>
                      <button v-else disabled class="btn btn-secondary">
                        <i class="fa-solid fa-check-circle me-2"></i>
                        Cours terminé
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Sidebar premium -->
              <div class="col-lg-4">
                <div v-if="upcomingCourses.length" class="card border-0 shadow-lg bg-gradient-primary text-white mb-4">
                  <div class="card-body p-4">
                    <h5 class="mb-3"><i class="fa-solid fa-star me-2"></i> Prochain cours</h5>
                    <h4 class="fw-bold">{{ upcomingCourses[0].matiere }}</h4>
                    <p class="mb-1 opacity-90">
                      <i class="fa-solid fa-calendar me-1"></i>
                      {{ formatDateFull(upcomingCourses[0].dateDebut) }}
                    </p>
                    <p class="mb-3 opacity-90">
                      <i class="fa-solid fa-clock me-1"></i>
                      {{ upcomingCourses[0].heureDebut }} – {{ upcomingCourses[0].heureFin }}
                    </p>
                    <a v-if="upcomingCourses[0].estEnLigne && isCourseActive(upcomingCourses[0])"
                       :href="upcomingCourses[0].lienVisio" target="_blank"
                       class="btn btn-light btn-sm w-100 fw-semibold">
                      <i class="fa-solid fa-video me-2"></i> Rejoindre maintenant
                    </a>
                  </div>
                </div>

                <div class="card border-0 shadow-sm">
                  <div class="card-body">
                    <h6 class="fw-bold mb-3"><i class="fa-solid fa-chart-pie me-2"></i> Cette semaine</h6>
                    <div class="stats-grid">
                      <div class="stat-item">
                        <span class="text-muted">Total</span>
                        <strong class="text-primary fs-4">{{ weeklyStats.total }}</strong>
                      </div>
                      <div class="stat-item">
                        <span class="text-muted">En ligne</span>
                        <strong class="text-success fs-4">{{ weeklyStats.online }}</strong>
                      </div>
                      <div class="stat-item">
                        <span class="text-muted">Présentiel</span>
                        <strong class="text-info fs-4">{{ weeklyStats.physical }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- === AUJOURD'HUI === -->
          <div v-if="activeTab === 'today'">
            <h4 class="fw-semibold mb-4">
              <i class="fa-solid fa-sun me-2 text-warning"></i>
              Cours du {{ formatDateFull(new Date()) }}
            </h4>

            <div v-if="isLoadingToday" class="text-center py-6">
              <div class="spinner-border text-primary" style="width: 3rem; height: 3rem;"></div>
            </div>

            <div v-else-if="!todayCourses.length" class="alert alert-success py-5 text-center border-0 shadow-sm">
              <i class="fa-solid fa-party-horn fa-3x mb-3 text-success"></i>
              <h5>Pas de cours aujourd'hui !</h5>
              <p class="text-muted">Profitez bien de votre journée</p>
            </div>

            <div v-else class="timeline-modern">
              <div v-for="cours in todayCourses" :key="cours.id" class="timeline-item-modern">
                <div class="timeline-dot" :class="{ 'active': isCourseActive(cours) }"></div>
                <div class="timeline-content-modern">
                  <div class="time-badge">{{ cours.heureDebut }} – {{ cours.heureFin }}</div>
                  <div class="course-box">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                      <h5 class="mb-0 fw-bold">{{ cours.matiere }}</h5>
                      <span :class="['badge fs-6', getCourseStatus(cours).class]">
                        {{ getCourseStatus(cours).text }}
                      </span>
                    </div>
                    <p class="text-muted mb-3">
                      <i class="fa-solid fa-user-tie me-2"></i>{{ cours.professeur }}
                    </p>

                    <template v-if="cours.estEnLigne && cours.lienVisio">
                      <a v-if="isCourseActive(cours)" :href="cours.lienVisio" target="_blank"
                         class="btn btn-success shadow-sm">
                        <i class="fa-solid fa-video me-2"></i> Rejoindre
                      </a>
                      <button v-else disabled class="btn btn-outline-secondary">
                        <i class="fa-solid fa-check me-2"></i> Terminé
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- === VUE HEBDOMADAIRE === -->
          <div v-if="activeTab === 'week'">
            <h4 class="fw-semibold mb-4">Emploi du temps hebdomadaire</h4>
            <div class="table-responsive">
              <table class="table weekly-table">
                <thead>
                  <tr>
                    <th>Horaires</th>
                    <th v-for="jour in joursOrdonnes" :key="jour">{{ jour }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="heure in heuresUniques" :key="heure">
                    <td class="time-col">{{ heure }}</td>
                    <td v-for="jour in joursOrdonnes" :key="jour">
                      <div v-for="cours in getCoursForDayAndTime(jour, heure)" :key="cours.id"
                           :class="['course-cell', { 'past': !isCourseActive(cours) }]">
                        <div class="cell-header">
                          <strong>{{ cours.matiere }}</strong>
                          <small class="text-muted d-block">{{ cours.professeur }}</small>
                        </div>
                        <div class="cell-footer">
                          <span class="time">{{ cours.heureDebut }}–{{ cours.heureFin }}</span>
                          <template v-if="cours.estEnLigne && cours.lienVisio">
                            <a v-if="isCourseActive(cours)" :href="cours.lienVisio" target="_blank"
                               class="btn btn-xs btn-success"><i class="fa-solid fa-video"></i></a>
                            <span v-else class="badge bg-secondary">Terminé</span>
                          </template>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import ApiService from "../../services/ApiService";
import { error, success } from "../../utils/utils";
import { useAuthStore } from '@/services/auth';

export default defineComponent({
  name: "MonAgenda",
  setup() {
    const authStore = useAuthStore();
    const activeTab = ref("upcoming");
    const isLoading = ref(false);
    const isLoadingToday = ref(false);
    const studentInfo = ref<any>(null);
    const upcomingCourses = ref<any[]>([]);
    const todayCourses = ref<any[]>([]);
    const allCourses = ref<any[]>([]);

    const joursOrdonnes = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

    const etudiantId = computed(() => authStore.user?.etudiant?.id || authStore.user?.id);

    const weeklyStats = computed(() => ({
      total: upcomingCourses.value.length,
      online: upcomingCourses.value.filter(c => c.estEnLigne).length,
      physical: upcomingCourses.value.filter(c => !c.estEnLigne).length,
    }));

    const heuresUniques = computed(() => {
      const heures = new Set<string>();
      allCourses.value.forEach(c => heures.add(c.heureDebut));
      return Array.from(heures).sort();
    });

    const getCoursForDayAndTime = (jour: string, heure: string) =>
      allCourses.value.filter(c => c.jour === jour && c.heureDebut === heure);

    const formatDateFull = (date: string | Date) =>
      new Date(date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

    // CLÉ DE VOÛTE : cours actif ?
    const isCourseActive = (cours: any): boolean => {
      const now = new Date();

      // Cas 1 : emploi du temps avec date de fin
      if (cours.periodeEmploiDuTemps?.fin) {
        if (new Date(cours.periodeEmploiDuTemps.fin) < now) return false;
      }

      // Cas 2 : date précise du cours (upcoming)
      if (cours.dateDebut) {
        const coursDate = new Date(cours.dateDebut);
        const today = new Date(); today.setHours(0,0,0,0);
        if (coursDate < today) return false;
        if (coursDate > today) return true;

        const [h, m] = cours.heureFin.split(":");
        const fin = new Date();
        fin.setHours(parseInt(h), parseInt(m), 0, 0);
        return now < fin;
      }

      // Cas 3 : cours récurrent → vérifier heure du jour
      const [h, m] = cours.heureFin.split(":");
      const finToday = new Date();
      finToday.setHours(parseInt(h), parseInt(m), 0, 0);
      return now < finToday;
    };

    const getCourseStatus = (cours: any) => {
      if (!isCourseActive(cours)) return { text: "Terminé", class: "bg-secondary" };
      return cours.estEnLigne
        ? { text: "En ligne", class: "bg-success" }
        : { text: "Présentiel", class: "bg-info" };
    };

    const loadAgenda = async () => {
      if (!etudiantId.value) return;
      isLoading.value = true;
      try {
        const res = await ApiService.get(`/etudiants/${etudiantId.value}/prochains-cours?days=7`);
        upcomingCourses.value = Array.isArray(res.data.data) ? res.data.data : [];
      } catch (err) { error("Impossible de charger l'agenda"); }
      finally { isLoading.value = false; }
    };

    const loadFullAgenda = async () => {
      try {
        const res = await ApiService.get(`/etudiants/${etudiantId.value}/agenda`);
        if (res.data.code === 200) {
          studentInfo.value = res.data.data.etudiant;
          allCourses.value = res.data.data.cours;
        }
      } catch (err) { console.error(err); }
    };

    const loadTodayCourses = async () => {
      isLoadingToday.value = true;
      try {
        const today = new Date().toISOString().split("T")[0];
        const res = await ApiService.get(`/etudiants/${etudiantId.value}/cours-du-jour?date=${today}`);
        todayCourses.value = res.data.data.cours || [];
      } catch (err) { error("Erreur chargement des cours du jour"); }
      finally { isLoadingToday.value = false; }
    };

    const refreshAgenda = async () => {
      await Promise.all([loadAgenda(), loadFullAgenda(), activeTab.value === 'today' && loadTodayCourses()]);
      success("Agenda actualisé !");
    };

    onMounted(() => {
      loadAgenda();
      loadFullAgenda();
    });

    return {
      activeTab, isLoading, isLoadingToday, studentInfo, upcomingCourses, todayCourses, allCourses,
      joursOrdonnes, weeklyStats, heuresUniques, formatDateFull, refreshAgenda,
      loadTodayCourses, getCoursForDayAndTime, isCourseActive, getCourseStatus
    };
  },
});
</script>

<style scoped>
.agenda-wrapper { background: linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%); min-height: 100vh; padding: 2rem 1rem; }
.agenda-header { background: white; padding: 2rem; border-radius: 1rem; box-shadow: 0 10px 30px rgba(0,0,0,0.05); margin-bottom: 1.5rem; }
.header-content { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem; }
.btn-refresh { border-radius: 50px; padding: 0.6rem 1.5rem; font-weight: 600; }

.premium-tabs .nav-link {
  border: none; border-bottom: 3px solid transparent; border-radius: 0;
  padding: 1rem 1.5rem; font-weight: 600; color: #6c757d; transition: all 0.3s;
}
.premium-tabs .nav-link:hover { color: #0d6efd; }
.premium-tabs .nav-link.active { color: #0d6efd; border-bottom-color: #0d6efd; background: transparent; }

.course-card-modern {
  background: white; border-radius: 1rem; padding: 1.5rem; box-shadow: 0 8px 25px rgba(0,0,0,0.08);
  transition: all 0.3s; border-left: 5px solid #0d6efd;
}
.course-card-modern:hover { transform: translateY(-4px); box-shadow: 0 15px 35px rgba(0,0,0,0.12); }
.btn-join { border-radius: 50px; padding: 0.75rem 1.5rem; font-weight: 600; }

.stats-grid { display: grid; gap: 1rem; }
.stat-item { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 0; border-bottom: 1px dashed #e9ecef; }
.stat-item:last-child { border-bottom: none; }

.timeline-modern { position: relative; padding-left: 2rem; }
.timeline-modern::before { content: ''; position: absolute; left: 14px; top: 0; bottom: 0; width: 4px; background: #e9ecef; border-radius: 2px; }
.timeline-item-modern { position: relative; margin-bottom: 2rem; }
.timeline-dot {
  position: absolute; left: -28px; top: 12px; width: 16px; height: 16px;
  background: #6c757d; border: 4px solid white; border-radius: 50%; box-shadow: 0 0 0 4px #e9ecef;
  transition: all 0.3s;
}
.timeline-dot.active { background: #0d6efd; box-shadow: 0 0 0 4px #0d6efd33; }
.time-badge { background: #0d6efd; color: white; padding: 0.35rem 0.75rem; border-radius: 50px; font-size: 0.85rem; font-weight: 600; display: inline-block; margin-bottom: 0.75rem; }
.course-box { background: white; border-radius: 1rem; padding: 1.25rem; box-shadow: 0 4px 15px rgba(0,0,0,0.06); }

.weekly-table { font-size: 0.9rem; }
.weekly-table th { background: #f8f9fa; font-weight: 600; text-align: center; }
.time-col { background: #f8f9fa; font-weight: 600; width: 90px; }
.course-cell {
  background: #f8f9fa; border-radius: 0.5rem; padding: 0.5rem; margin: 0.25rem 0;
  transition: all 0.2s; min-height: 70px;
}
.course-cell.past { opacity: 0.5; background: #f1f1f1; }
.cell-header { margin-bottom: 0.25rem; }
.cell-footer { display: flex; justify-content: space-between; align-items: center; font-size: 0.8rem; margin-top: 0.5rem; }

@media (max-width: 768px) {
  .header-content { flex-direction: column; text-align: center; }
  .premium-tabs .nav-link { padding: 0.75rem 1rem; font-size: 0.9rem; }
}
</style>